import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  useTheme,
} from "@mui/material";

interface PricingRow {
  days: string;
  monthly?: string;
  yearly?: string;
  academic?: string;
  meals?: string;
  fullBoard?: string;
  withoutMeals?: string;
}

interface PricingTableProps {
  currentPackage: string;
  activeTab: number;
  academicYear: string;
  academicYearKey: string;
}

const PricingTable: React.FC<PricingTableProps> = ({
  currentPackage,
  activeTab,
  academicYear,
  academicYearKey,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const navy = theme.palette.text.secondary;
  const orange = theme.palette.warning.main;
  const softHeaderBg = "rgba(34, 74, 133, 0.04)";
  const softStripe = "rgba(34, 74, 133, 0.025)";

  return (
    <Box>
      {/* Section header — year is the hero, unmistakable label */}
      <Box sx={{ textAlign: "center", mb: 5 }}>
        <Typography
          variant="h3"
          fontWeight={800}
          color={navy}
          sx={{
            letterSpacing: "-0.01em",
            lineHeight: 1.15,
            mb: 1.5,
          }}
        >
          {t(`PricingPage.PricingFrame.academicYear.${academicYear}`)}
        </Typography>
        <Box
          sx={{
            width: 56,
            height: 3,
            backgroundColor: orange,
            borderRadius: 2,
            mx: "auto",
            mb: 2,
          }}
        />
        <Typography
          variant="h6"
          fontWeight={500}
          color={navy}
          sx={{ opacity: 0.75 }}
        >
          {t(`PricingPage.PricingFrame.${currentPackage}.titleTable`)}
        </Typography>
      </Box>

      {Object.entries(
        t(`PricingPage.PricingFrame.${currentPackage}.pricingTable.${academicYearKey}`, {
          returnObjects: true,
        })
      ).map(([type, rows], index, arr) => (
        <Box key={index} mb={index < arr.length - 1 ? 5 : 0}>
          {/* Day-type heading — left accent bar + clean typography */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Box
              sx={{
                width: 4,
                height: 22,
                backgroundColor: orange,
                borderRadius: 2,
                mr: 1.5,
              }}
            />
            <Typography variant="h5" fontWeight={700} color={navy}>
              {type === "fullDay"
                ? t("PricingPage.PricingFrame.fullDay")
                : t("PricingPage.PricingFrame.halfDay")}
            </Typography>
          </Box>

          <TableContainer
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "12px",
              overflowY: "hidden",
              overflowX: "auto",
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: softHeaderBg }}>
                  <TableCell
                    sx={{
                      color: navy,
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    {t("PricingPage.PricingFrame.headersTable.days")}
                  </TableCell>
                  {activeTab === 0 ? (
                    <>
                      <TableCell
                        sx={{
                          color: navy,
                          fontWeight: 700,
                          fontSize: "0.85rem",
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        {t("PricingPage.PricingFrame.headersTable.monthly")}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: navy,
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          opacity: 0.75,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        {t("PricingPage.PricingFrame.headersTable.yearly")}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: navy,
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          opacity: 0.75,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        {t("PricingPage.PricingFrame.headersTable.academic")}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: navy,
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          opacity: 0.75,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        {t("PricingPage.PricingFrame.headersTable.meals")}
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell
                        sx={{
                          color: navy,
                          fontWeight: 700,
                          fontSize: "0.85rem",
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        {t("PricingPage.PricingFrame.headersTable.fullBoard")}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: navy,
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          opacity: 0.75,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        {t("PricingPage.PricingFrame.headersTable.withoutMeals")}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {(rows as PricingRow[]).map((row, idx, rowsArr) => (
                  <TableRow
                    key={idx}
                    sx={{
                      "&:nth-of-type(odd)": {
                        backgroundColor: softStripe,
                      },
                      "& td": {
                        borderBottom:
                          idx === rowsArr.length - 1
                            ? "none"
                            : `1px solid ${theme.palette.divider}`,
                        py: 2,
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600, color: navy }}>
                      {row.days}
                    </TableCell>
                    {activeTab === 0 ? (
                      <>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            fontSize: "1.05rem",
                            color: navy,
                          }}
                        >
                          {row.monthly}
                        </TableCell>
                        <TableCell sx={{ color: "text.primary", opacity: 0.85 }}>
                          {row.yearly}
                        </TableCell>
                        <TableCell sx={{ color: "text.primary", opacity: 0.85 }}>
                          {row.academic}
                        </TableCell>
                        <TableCell sx={{ color: "text.primary", opacity: 0.85 }}>
                          {row.meals}
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            fontSize: "1.05rem",
                            color: navy,
                          }}
                        >
                          {row.fullBoard}
                        </TableCell>
                        <TableCell sx={{ color: "text.primary", opacity: 0.85 }}>
                          {row.withoutMeals}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </Box>
  );
};

export default PricingTable;
